import { jsPDF } from 'jspdf'
import { AudienceParams } from '@/pdf/types'
import { renderHtmlToImage } from '@/pdf/htmlRenderer'

const PRIMARY_COLOR = '#2e5eaa'

const hexToRgb = (hex: string): [number, number, number] => {
    const normalizedHex = hex.replace('#', '')
    const r = parseInt(normalizedHex.substring(0, 2), 16)
    const g = parseInt(normalizedHex.substring(2, 4), 16)
    const b = parseInt(normalizedHex.substring(4, 6), 16)
    return [r, g, b]
}

const formatarDataBR = (date: string): string => {
    const parsedDate = new Date(date)
    if (Number.isNaN(parsedDate.getTime())) return date
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(parsedDate)
}

const normalizeBase64Image = (
    base64: string
): { src: string; format: 'PNG' | 'JPEG' } => {
    if (base64.startsWith('data:image/')) {
        const format =
            base64.startsWith('data:image/jpeg') ||
            base64.startsWith('data:image/jpg')
                ? 'JPEG'
                : 'PNG'
        return { src: base64, format }
    }

    return {
        src: `data:image/png;base64,${base64}`,
        format: 'PNG'
    }
}

const escapeHtml = (str: unknown): string => {
    const s = String(str ?? '')
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

export const buildCoverHtml = (parametros: AudienceParams): string => {
    const programText = parametros.program
        ? `<p class="report-info">
             Métricas obtidas durante o programa
             <span class="program-highlight">${parametros.program}</span>
           </p>`
        : ''

    return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>MediaKit - Audiência Digital</title>

<style>
:root {
    --cor-primaria: #2e5eaa;
}

body {
    background:#fafafa;
    font-family:Arial,sans-serif;
    margin:0;
}

.page-container {
    width:210mm;
    height:297mm;
    padding:20mm;
    position:relative;
}

.capa-content {
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:center;
    text-align:center;
    height:100%;
}

.company-card {
    border-radius:10px;
    box-shadow:0 10px 25px rgba(0,0,0,0.1);
    padding:10mm;
    width:80%;
    margin-bottom:10mm;
}

.company-logo {
    width:70mm;
    border-radius:10%;
}

.company-name {
    font-size:24pt;
    font-weight:bold;
    color:var(--cor-primaria);
}

.period-card {
    border-radius:8px;
    box-shadow:0 4px 15px rgba(0,0,0,0.08);
    padding:5mm 10mm;
    margin-top:5mm;
    width:80%;
}

.report-info {
    font-size:12pt;
    color:#555;
}

.date-highlight {
    color:var(--cor-primaria);
    font-weight:bold;
}

.program-highlight {
    color:var(--cor-primaria);
    font-weight:bold;
}

.highlight-box {
    background: rgba(46,94,170,0.05);
    border-left: 3px solid var(--cor-primaria);
    border-radius: 8px;
    padding: 4mm;
    margin-top: 5mm;
    justify-self: stretch;
    text-align: justify;
}

.highlight-text {
    font-size:9pt;
    color:#5f5f5f;
}

    .highlight-clean {
    border: none;
    background: none;
    border-radius: 8px;
    padding: 4mm;
    margin-top: 5mm;
    text-align: justify;
}

.highlight-clean p {
    font-size: 7.5pt;
    color: #555;
    margin: 0;
}
</style>
</head>

<body>

<div class="page-container">

<div class="capa-content">

${parametros.logoBase64 ? `<div class="company-card"><img src="${normalizeBase64Image(parametros.logoBase64).src}" class="company-logo"/></div>` : ''}

<div class="company-name">
${parametros.multipleRadiosMode ? 'Relatório Consolidado' : parametros.companyName}
</div>

<div class="period-card">

<p class="report-info">
Período de análise:
<span class="date-highlight">${formatarDataBR(parametros.from)}</span>
a
<span class="date-highlight">${formatarDataBR(parametros.to)}</span>
</p>

${programText}

</div>

${parametros.insight_geral ? `
<div class="highlight-box" style="width:100%;box-sizing:border-box;">
    <p class="highlight-text">${escapeHtml(parametros.insight_geral)}</p>
</div>` : ''}
</div>

</div>

</body>
</html>
`
}

export const addCoverPage = async (pdf: jsPDF, parametros: AudienceParams): Promise<void> => {
    const img = await renderHtmlToImage(buildCoverHtml(parametros))
    pdf.addImage(img, 'JPEG', 0, 0, 210, 297)
}

// ── Legacy jsPDF implementation kept below as reference ──────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _addCoverPageLegacy = (pdf: jsPDF, parametros: AudienceParams): void => {
    const [primaryR, primaryG, primaryB] = hexToRgb(PRIMARY_COLOR)

    const pageWidth = 210
    const pageHeight = 297
    const centerX = pageWidth / 2

    let cursorY = 48

    pdf.setFillColor(242, 242, 245)
    pdf.rect(0, 0, pageWidth, pageHeight, 'F')

    const contentWidth = 132
    const contentX = (pageWidth - contentWidth) / 2

    if (parametros.logoBase64) {
        const { src, format } = normalizeBase64Image(parametros.logoBase64)

        const logoWidth = 72
        const logoHeight = 34
        const logoX = centerX - logoWidth / 2
        const logoY = cursorY

        try {
            pdf.addImage(src, format, logoX, logoY, logoWidth, logoHeight)
        } catch {
            pdf.setTextColor(120, 120, 120)
            pdf.setFontSize(10)
            pdf.text(
                'Logo não disponível',
                centerX,
                cursorY + 18,
                { align: 'center' }
            )
        }
    }

    cursorY += 48

    if (parametros.multipleRadiosMode) {
        pdf.setTextColor(primaryR, primaryG, primaryB)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(20)
        pdf.text('Relatório Consolidado', centerX, cursorY, { align: 'center' })

        pdf.setTextColor(95, 95, 95)
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(11)
        pdf.text('Este é um relatório sobre todas as rádios', centerX, cursorY + 9, {
            align: 'center'
        })
        pdf.text('associadas ao seu usuário', centerX, cursorY + 15, {
            align: 'center'
        })
        cursorY += 26
    } else {
        pdf.setTextColor(primaryR, primaryG, primaryB)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(16)
        pdf.text(parametros.companyName || '-', centerX, cursorY, { align: 'center' })
        cursorY += 18
    }

    pdf.setTextColor(95, 95, 95)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    const periodLabel = 'Período de análise:'
    const periodLabelWidth = pdf.getTextWidth(periodLabel)

    const fromText = formatarDataBR(parametros.from)
    const toText = formatarDataBR(parametros.to)
    const datePadding = 2.5
    const dateHeight = 7

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    const fromWidth = pdf.getTextWidth(fromText) + (datePadding * 2)
    const toWidth = pdf.getTextWidth(toText) + (datePadding * 2)

    const totalWidth = periodLabelWidth + 4 + fromWidth + 4 + pdf.getTextWidth('a') + 4 + toWidth
    const periodStartX = centerX - (totalWidth / 2)
    const periodY = cursorY

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    pdf.setTextColor(95, 95, 95)
    pdf.text(periodLabel, periodStartX, periodY)

    let dynamicX = periodStartX + periodLabelWidth + 4

    pdf.setFillColor(225, 234, 247)
    pdf.roundedRect(dynamicX, periodY - 5.3, fromWidth, dateHeight, 1.5, 1.5, 'F')
    pdf.setTextColor(primaryR, primaryG, primaryB)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text(fromText, dynamicX + datePadding, periodY - 0.6)

    dynamicX += fromWidth + 4

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    pdf.setTextColor(95, 95, 95)
    pdf.text('a', dynamicX, periodY)

    dynamicX += pdf.getTextWidth('a') + 4

    pdf.setFillColor(225, 234, 247)
    pdf.roundedRect(dynamicX, periodY - 5.3, toWidth, dateHeight, 1.5, 1.5, 'F')
    pdf.setTextColor(primaryR, primaryG, primaryB)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text(toText, dynamicX + datePadding, periodY - 0.6)

    let innerY = cursorY + 12

    if (parametros.program) {
        pdf.setTextColor(85, 85, 85)
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(10)
        pdf.text(
            'Métricas obtidas durante o programa',
            centerX,
            innerY,
            { align: 'center' }
        )

        pdf.setTextColor(primaryR, primaryG, primaryB)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(10)
        pdf.text(parametros.program, centerX, innerY + 6, { align: 'center' })

        innerY += 14
    }

    const infoX = contentX
    const infoY = innerY + 2
    const infoW = contentWidth
    const infoH = 24

    pdf.setFillColor(229, 232, 238)
    pdf.roundedRect(infoX, infoY, infoW, infoH, 2, 2, 'F')

    pdf.setDrawColor(primaryR, primaryG, primaryB)
    pdf.setLineWidth(1)
    pdf.line(infoX, infoY, infoX, infoY + infoH)

    pdf.setTextColor(85, 85, 85)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8.5)

    pdf.setTextColor(119, 119, 119)
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'italic')
   
}